/* globals LANGUAGE_CODE */
/* Load 'date_navigation' on any page that needs it.
  The page is expected to have HTML like this:

 <p class="date-selector">
 <a href="" class="nav-arrow arrow-left">Prev</a>
 <span>{{ today }}</span>
 <a href="" class="nav-arrow arrow-right disabled">Next</a>
 </p>

 The user can click the Prev or Next icon to move backward or forward.
 Next is disabled if going forward would be the future.

 Code can call date_nav.get_selected_date() to get the currently selected date.

 Code can also register for notification on changes with something like:

     date_nav.on('change', function(selected_date) {
        ...
     });

 The change is signalled /after/ the page has been updated.

 Events use Backbone.Events so see backbone doc for more information.

 By default, dates are shown in the control like "Dec 20" and each
 click changes the date by one day.

 Do date_nav.set_navigate_by('week') to navigate by weeks instead. Each
 click moves forward or back by one week. And two dates are shown, the date
 of the sunday and the following saturday, e.g. "Dec 16 - Dec 22".
 (Call .render() after calling set_navigate_by).

 Do date_nav.set_navigate_by('month') to navigate by months instead.
 The date is not forced to any particular day. Clicking forward or
 back will change the month, not the day of the month. Two
 dates are shown, the date one month previous and the currently
 selected date. E.g. if the selected date is Dec. 22, it will
 display "Nov 22 - Dec 22".

 When navigating by month, the selected_date will be the /last/
 date in the range (that seems to be how the old version worked).

 Specify a collection during initialization and do
 date_nav.set_navigate_by('collection') to navigate by entries in a collection
 with dates. Clicking forward or back will change to the date of the next
 or previous entry in the collection respectively.

 */

import $ from 'exports-loader?jQuery!picker.date';
import _ from 'underscore';
import Backbone from 'backbone';


// Private utilities

var next_month = function (datetime) {
  // return Date() with the same day of the month, in the next month
  // except if that would be invalid (e.g. Feb. 30), returns
  // the last day of that month
  var new_datetime = new Date(datetime);
  new_datetime.setMonth(new_datetime.getMonth() + 1);
  return new_datetime;
};

var previous_month = function (datetime) {
  // return Date() with the same day of the month, in the previous month
  // except if that would be invalid (e.g. Feb. 30), returns
  // the last day of that month
  var new_datetime = new Date(datetime);
  new_datetime.setMonth(new_datetime.getMonth() - 1);
  return new_datetime;
};

var next_week = function (datetime) {
  // return Date() with the same day of the week, in the next week
  var new_datetime = new Date(datetime);
  new_datetime.setDate(new_datetime.getDate() + 7);
  return new_datetime;
};

var previous_week = function (datetime) {
  // return Date() with the same day of the week, in the previous week
  var new_datetime = new Date(datetime);
  new_datetime.setDate(new_datetime.getDate() - 7);
  return new_datetime;
};

var next_fiveday = function (datetime) {
  // return Date() + 5 days
  var new_datetime = new Date(datetime);
  new_datetime.setDate(new_datetime.getDate() + 5);
  return new_datetime;
};

var previous_fiveday = function (datetime) {
  // return Date() - 5 days
  var new_datetime = new Date(datetime);
  new_datetime.setDate(new_datetime.getDate() - 5);
  return new_datetime;
};

var next_day = function (datetime) {
  // return Date() with the next day
  var new_datetime = new Date(datetime);
  new_datetime.setDate(new_datetime.getDate() + 1);
  return new_datetime;
};

var previous_day = function (datetime) {
  // return Date() with the previous day
  var new_datetime = new Date(datetime);
  new_datetime.setDate(new_datetime.getDate() - 1);
  return new_datetime;
};

// STATIC METHODS
var DateNav = {
  // parse a date in yyyy-mm-dd format (or yyyy/mm/dd or yyyy.mm.dd, any non-digit separator should work)
  // see http://stackoverflow.com/questions/2587345/javascript-date-parse
  parse_date: function (input) {
    try {
      var parts = input.match(/(\d+)/g);
      // new Date(year, month [, date [, hours[, minutes[, seconds[, ms]]]]])
      var month = parts[1] - 1,
        day = parseInt(parts[2], 10); // months are 0-based
      var date = new Date(parts[0], month, day);
      // Even though an exception wasn't raised, there may still be an error
      if (isNaN(date) || date.getMonth() !== month || date.getDate() !== day) {
        return new Date();
      }
      return date;
    } catch (e) {
      // The passed input is not a valid date, return today
      return new Date();
    }
  },

  y_m_d: function (datetime, leading_zeros) {
    if (typeof leading_zeros === 'undefined') {
      leading_zeros = true;
    }
    // this would be easier with toISOString(), but IE 8 doesn't have it
    var y = String(datetime.getFullYear());
    var m = String(datetime.getMonth() + 1);
    var d = String(datetime.getDate());
    if (leading_zeros) {
      m = (m.length === 1 ? '0' : '') + m;
      d = (d.length === 1 ? '0' : '') + d;
    }
    return [ y, m, d ];
  },

  formatted_date: function (datetime, leading_zeros) {
    // return the given date formatted as "YYYY-MM-DD"
    // if no date is passed, formats the selected date
    var y_m_d = DateNav.y_m_d(datetime, leading_zeros);
    return y_m_d[0] + '-' + y_m_d[1] + '-' + y_m_d[2];
  },

  us_date: function (datetime, leading_zeros) {
    // return the given date formatted as "MM-DD-YYYY"
    // if no date is passed, formats the selected date
    var y_m_d = DateNav.y_m_d(datetime, leading_zeros);
    return y_m_d[1] + '-' + y_m_d[2] + '-' + y_m_d[0];
  },

  just_the_date: function (datetime) {
    // given a Date() object, return the date with time fields set to 0
    datetime.setHours(0);
    datetime.setMinutes(0);
    datetime.setSeconds(0);
    datetime.setMilliseconds(0);
    return datetime;
  },

  format_month_day: function (month, day) {
    if (LANGUAGE_CODE == 'en') {
      return month + ' ' + day;
    }
    return day + ' ' + month;

  },

  month_day: function (datetime) {
    // given a date, return it formatted like "Dec 20"
    var month = $.fn.pickadate.defaults.monthsShort[datetime.getMonth()];
    return DateNav.format_month_day(month, datetime.getDate());
  },

  month: function (datetime) {
    // given a date, return it formatted like "Dec"
    return $.fn.pickadate.defaults.monthsShort[datetime.getMonth()];
  },

  long_month_day: function (datetime) {
    // given a date, return it formatted like "December 20"
    var month = $.fn.pickadate.defaults.monthsFull[datetime.getMonth()];
    return DateNav.format_month_day(month, datetime.getDate());
  },

  weekday_month_day: function (datetime) {
    // given a date, return it formatted like "Dec 20"
    var month = $.fn.pickadate.defaults.monthsFull[datetime.getMonth()];
    var weekday = $.fn.pickadate.defaults.weekdaysFull[datetime.getDay()];
    return weekday + ', ' + DateNav.format_month_day(month, datetime.getDate());
  }
};

// Any method on this object that doesn't start with "_" is part of the public API
export default Backbone.Router.extend({
  initialize: function (options) {
    _.bindAll.apply(_, [this].concat(_.functions(this)));

    this.set_language(LANGUAGE_CODE);

    // Set pager to something more specific if there are multiple pagers on a page
    options = options || {};
    this.selected_date = new Date();
    this.query_string = window.location.search;
    this.pagination = options.pagination;
    this.pager = options.pager || $('.pager', this.pagination);
    this.calendar = $('.calendar-widget-input', $(this.pagination));
    this.start_on_sunday = true;
    if (options.start_on_sunday !== undefined) {
      this.start_on_sunday = options.start_on_sunday;
    }

    // Add an optional collection for collection navigation
    this.collection = options.collection || null;

    // Handle pager clicks
    $('.arrow-right', this.pager).on('click', this.click_right);
    $('.arrow-left', this.pager).on('click', this.click_left);

    // Handle calendar open
    if (this.calendar.length > 0) {
      $('.calendar-widget a', this.pagination).on('click', this.date_picker_clicked);
    }

    this.initialize_date(options.date);
  },

  // Set the date to `date` if defined. Otherwise use the date from the
  // get params if that is in an acceptable range. Fallback to today.
  initialize_date: function (date) {
    var datetime = this.selected_date; // today
    if (date === undefined) {
      var get_params = this.get_query_params();
      if (get_params.date) {
        datetime = DateNav.parse_date(get_params.date);
        if (datetime > this.selected_date || datetime.getFullYear() < 1900) {
          datetime = this.selected_date;
        }
      }
    }
    this.change_date(date || datetime);
  },

  // Convert query string to javascript object
  // http://www.timetler.com/2013/11/14/location-search-split-one-liner/
  get_query_params: function () {
    return _.chain(this.query_string.slice(1).split('&'))
      .map(function (item) {
        if (item) {
          return item.split('=');
        }
      })
      .compact()
      .object()
      .value();
  },

  // Convert javascript object to query string
  set_query_params: function (new_params) {
    var query_params = _.defaults(
      new_params,
      this.get_query_params(this.query_string)
    );
    var query_string = _.reduce(query_params, function (memo, val, key) {
      return memo + key + '=' + val + '&';
    }, '?');
    // Strip the trailing '&'
    this.query_string = query_string.slice(0, -1);
  },

  set_language: function (language) {
    // These language definitions come from
    // https://github.com/amsul/pickadate.js/tree/3.5.6/lib/translations
    if (language == 'de') {
      $.extend($.fn.pickadate.defaults, {
        monthsFull: [ 'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember' ],
        monthsShort: [ 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez' ],
        weekdaysFull: [ 'Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag' ],
        weekdaysShort: [ 'So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa' ],
        today: 'Heute',
        clear: 'Löschen',
        close: 'Schließen',
        firstDay: 1,
        format: 'dddd, dd. mmmm yyyy',
        formatSubmit: 'yyyy/mm/dd'
      });
    } else if (language == 'fr') {
      $.extend($.fn.pickadate.defaults, {
        monthsFull: [ 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre' ],
        monthsShort: [ 'Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec' ],
        weekdaysFull: [ 'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi' ],
        weekdaysShort: [ 'Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam' ],
        today: 'Aujourd\'hui',
        clear: 'Effacer',
        close: 'Fermer',
        firstDay: 1,
        format: 'dd mmmm yyyy',
        formatSubmit: 'yyyy/mm/dd',
        labelMonthNext: 'Mois suivant',
        labelMonthPrev: 'Mois précédent',
        labelMonthSelect: 'Sélectionner un mois',
        labelYearSelect: 'Sélectionner une année'
      });
    } else if (language == 'tr') {
      $.extend($.fn.pickadate.defaults, {
        monthsFull: [ 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık' ],
        monthsShort: [ 'Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara' ],
        weekdaysFull: [ 'Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi' ],
        weekdaysShort: [ 'Pzr', 'Pzt', 'Sal', 'Çrş', 'Prş', 'Cum', 'Cmt' ],
        today: 'bugün',
        clear: 'sil',
        firstDay: 1,
        format: 'dd mmmm yyyy dddd',
        formatSubmit: 'yyyy/mm/dd'
      });
    } else if (language == 'ru') {
      $.extend($.fn.pickadate.defaults, {
        monthsFull: [ 'января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря' ],
        monthsShort: [ 'янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек' ],
        weekdaysFull: [ 'воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота' ],
        weekdaysShort: [ 'вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб' ],
        today: 'сегодня',
        clear: 'удалить',
        close: 'закрыть',
        firstDay: 1,
        format: 'd mmmm yyyy г.',
        formatSubmit: 'yyyy/mm/dd'
      });
    } else {
      // Default to English
      $.extend($.fn.pickadate.defaults, {
        monthsFull: [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ],
        monthsShort: [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ],
        weekdaysFull: [ 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday' ],
        weekdaysShort: [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ],
        today: 'Today',
        clear: 'Clear',
        firstDay: undefined,
        format: 'd mmmm, yyyy',
        formatSubmit: undefined
      });
    }
  },

  change_date: function (datetime, options) {
    // change the selected date to the one given, update the page,
    // notify any listeners, etc etc.
    var previous_date = this.get_selected_date();
    this.selected_date = DateNav.just_the_date(datetime);

    this.render();
    this.set_query_params({ date: this.formatted_date(this.selected_date) });
    // notify listeners, change push state, only if silent != true
    if (!_.contains(_.keys(options || {}), 'silent') || !options.silent) {
      window.history.pushState(
        {},
        document.title,
        window.location.origin + window.location.pathname + this.query_string
      );
      this.trigger('change', this.get_selected_date(), previous_date);
    }
  },

  change_date_by_string: function (s, options) {
    // like change_date, but takes a string representing a date in format "YYYY-MM-DD"
    this.change_date(DateNav.parse_date(s), options);
  },

  decrement: function () {
    // Change the selected date to the previous day, week, or month
    var new_date;
    var navigate_by = this.get_navigate_by();
    if (navigate_by === 'month') {
      new_date = previous_month(this.selected_date);
    } else if (navigate_by === 'week') {
      new_date = previous_week(this.selected_date);
    } else if (navigate_by === 'fiveday') {
      new_date = previous_fiveday(this.selected_date);
    } else if (navigate_by === 'collection') {
      var self = this;
      var later_models = this.collection.filter(function (model) {
        return DateNav.parse_date(model.get('date')) < self.selected_date;
      });
      new_date = DateNav.parse_date(later_models[later_models.length - 1].get('date'));
    } else {
      new_date = previous_day(this.selected_date);
    }
    setTimeout($.proxy(function () { this.change_date(new_date); }, this), 0);
    setTimeout(this.update_picker_date, 0);
  },

  formatted_date: function (datetime, leading_zeros) {
    return DateNav.formatted_date(datetime || this.selected_date, leading_zeros);
  },

  get_dow: function (iso_date) {
    // Return the 3 character day-of-week for a given ISO date
    var datetime = date_nav.parse_date(iso_date);
    var day_num = datetime.getDay();
    var dow = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ];
    return dow[day_num];
  },

  get_selected_date: function () {
    // return the currently selected date, as a Date() object
    // (copy so caller cannot change directly)
    return new Date(this.selected_date);
  },

  increment: function () {
    // Change the selected date to the next day, week, or month
    var newDate;
    var today = new Date();
    var navigate_by = this.get_navigate_by();
    if (navigate_by === 'month') {
      newDate = next_month(this.selected_date);
    } else if (navigate_by === 'week') {
      newDate = next_week(this.selected_date);
    } else if (navigate_by === 'fiveday') {
      newDate = next_fiveday(this.selected_date);
    } else if (navigate_by == 'collection') {
      var self = this;
      var later_models = this.collection.filter(function (model) {
        return DateNav.parse_date(model.get('date')) > self.selected_date;
      });
      newDate = DateNav.parse_date(later_models[0].get('date'));
    } else {
      newDate = next_day(this.selected_date);
    }
    setTimeout($.proxy(function () {
      this.change_date(newDate > today ? today : newDate);
    }, this), 0);
    setTimeout(this.update_picker_date, 0);
  },

  _is_before_range: function () {
    // return true if moving backward would enter the time before our range (or go farther into it)
    var today = DateNav.just_the_date(new Date());
    var navigate_by = this.get_navigate_by();
    if (navigate_by === 'collection') {
      today = this.selected_date;
      return _.isEmpty(this.collection.filter(function (model) {
        return DateNav.parse_date(model.get('date')) < today;
      }));
    }
    return false;

  },

  _is_future: function () {
    // return true if moving forward would enter the future (or go farther into it)
    var today = DateNav.just_the_date(new Date());
    var navigate_by = this.get_navigate_by();
    if (navigate_by === 'collection') {
      today = this.selected_date;
      return _.isEmpty(this.collection.filter(function (model) {
        return DateNav.parse_date(model.get('date')) > today;
      }));
    }
    return today.getTime() <= this.end_date().getTime();

  },

  click_right: function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this._is_future()) {
      setTimeout(this.increment, 0);
    }
  },

  click_left: function (e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this._is_before_range()) {
      setTimeout(this.decrement, 0);
    }
  },

  // set this to 'week' or 'month' to move by weeks or months; see comment above
  // set to 'collection' to base movement on a collection of models with dates
  set_navigate_by: function (period) {
    this.pager.data('navigate_by', period);
  },

  get_navigate_by: function () {
    var navigate_by = this.pager.data('navigate_by');
    if (!navigate_by) {
      navigate_by = 'day';
      this.set_navigate_by(navigate_by);
    }
    return navigate_by;
  },

  pretty_month_day: function (iso_date) {
    // / Convert ISO Date string ("2012-12-09") to string '12 / 9'
    // (no leading zeroes)
    var m = String(Number(iso_date.substr(5, 2)));
    var d = String(Number(iso_date.substr(8, 2)));
    return m + ' / ' + d;
  },

  // Date picker stuff
  date_picker: null,

  date_picker_select: function (e) {
    if (e.select) {
      this.change_date(new Date(e.select));
    }
  },

  date_picker_clicked: function (e) {
    e.stopPropagation();
    e.preventDefault();
    this.date_picker.open(false);
    $(document).one('click', this.date_picker.close);
  },

  create_date_picker: function () {
    if (this.calendar.length === 0) {
      return;
    }
    var calendar_input = this.calendar.pickadate();
    this.date_picker = calendar_input.pickadate('picker');
    // Use 'true' for 'today'
    this.date_picker.set('max', true);
    this.date_picker.set('select', this.selected_date);
    this.date_picker.on('set', this.date_picker_select);
  },

  update_picker_date: function () {
    // Update the date picker
    if (this.date_picker) {
      // Destroy and recreate the picker, to prevent an infinite loop
      this.date_picker.stop();
      this.create_date_picker();
    }
  },

  current_date_txt: function (navigate_by) {
    if (navigate_by === 'day' || navigate_by === 'collection') {
      return DateNav.long_month_day(this.selected_date);
    } else if (navigate_by === 'week' || navigate_by === 'month' || navigate_by === 'fiveday') {
      return DateNav.long_month_day(this.start_date()) + ' - ' +
                  (this.start_date().getMonth() == this.end_date().getMonth() ?
                    this.end_date().getDate() :
                    DateNav.long_month_day(this.end_date()));
    }
    return '';
  },

  render: function () {
    // update the page to display the selected date
    var navigate_by = this.get_navigate_by();
    $('.pager-date', this.pager).text(this.current_date_txt(navigate_by));

    // Date Picker
    if (!this.date_picker) {
      this.create_date_picker();
    }
    if (this._is_future()) {
      $('.arrow-right', this.pager).addClass('disabled');
    } else {
      $('.arrow-right', this.pager).removeClass('disabled');
    }
    if (this._is_before_range()) {
      $('.arrow-left', this.pager).addClass('disabled');
    } else {
      $('.arrow-left', this.pager).removeClass('disabled');
    }
  },

  start_date: function () {
    // return the first date that should be displayed
    // Depends on navigate_by:
    //    'day'     => selected_date
    //    'fiveday' => sunday before selected_date or 4 days earlier
    //    'week'    => sunday before selected_date or 6 days earlier
    //    'month'   => one month prior to selected_date
    var datetime = this.selected_date;
    var navigate_by = this.get_navigate_by();
    if (navigate_by === 'month') {
      datetime = previous_month(this.selected_date);
    } else if (navigate_by === 'week' || navigate_by === 'fiveday') {
      var diff = navigate_by === 'week' ? 6 : 4;
      if (this.start_on_sunday) {
        // nearest previous sunday is .getDay() days previous (could be zero)
        diff = this.selected_date.getDay();
      }
      datetime = new Date(this.selected_date);
      if (diff !== 0) {
        datetime.setDate(datetime.getDate() - diff);
      }
    }
    return datetime;
  },

  end_date: function () {
    // for use when navigating by week, returns the last day in the span (saturday)
    // (just in case, returns the selected day if not navigating by week)
    // see also start_date
    var datetime = this.selected_date;
    var navigate_by = this.get_navigate_by();
    if (navigate_by === 'week' || navigate_by === 'fiveday') {
      var diff = 0;
      if (this.start_on_sunday) {
        // the nearest Saturday (for 'week', Thursday for 'fiveday'),
        // today or in the future -- 6 = Saturday, 4 = Thursday
        diff = (navigate_by === 'week' ? 6 : 4) - this.selected_date.getDay();
      }
      datetime = new Date(this.selected_date);
      if (diff !== 0) {
        datetime.setDate(datetime.getDate() + diff);
      }
    }
    return datetime;
  },

  days_in_range: function () {
    return Math.round((this.end_date() - this.start_date()) / 24 / 60 / 60 / 1000);
  },

  non_future_days_in_range: function () {
    return Math.ceil((new Date() - this.start_date()) / 24 / 60 / 60 / 1000);
  }
}, DateNav);



//////////////////
// WEBPACK FOOTER
// ./node_modules/orcas-core/core/static/js/core/controllers/date_navigation.js
// module id = 5
// module chunks = 0